import * as React from 'react';
import * as Moment from 'moment';
import { Form, useFormApi } from 'informed';
import { TextBox } from '../forms/informedv2/TextBox';
import AsyncActionButton from '../misc/components/AsyncActionButton';
import { ErrorBoundary } from '../errors/ErrorBoundary';
import { Checkbox } from '../forms/informedv2/Checkbox';
import { ConfigFieldTextBox } from '../forms/informedv2/ConfigFieldTextBox';
import { ConfigFieldDatePicker } from '../forms/informedv2/ConfigFieldDatePicker';
import { ConfigurableDataItemIds } from '../misc/api/ApiConfigurableDataItems';
import { useWebPortalApiClient } from '../misc/api/WebPortalTokenClient';
import { DatePickerAcceptDateRange } from '../forms/informedv2/DatePicker';

export interface RandomizationModalBodyProps {
    studyId: string;
    subjectId: string;
    initialValues: RandomizationForm;
    onClose: () => void;
    onSubmit: (values: RandomizationForm) => void;
}

export interface RandomizationForm {
    SubjectNumber: string;
    IsRandomized: boolean;
    RandomizationNumber: string;
    RandomizationDate: string;
}

const RandomizationModalBodyForm = ({ studyId, subjectId, initialValues, onClose, onSubmit, children}: React.PropsWithChildren<RandomizationModalBodyProps>) => {
    const formApi = useFormApi<RandomizationForm>();
    const [checked, setChecked] = React.useState(false);
    const [submitting, setSubmitting] = React.useState(false);

    const client = useWebPortalApiClient();

    const formatString =  "YYYY-MM-DD HH:mm";

    React.useEffect(() => {
        setChecked(initialValues.IsRandomized);
    }, []);

    React.useEffect(() => {
        if (formApi && checked) {
            if (formApi.getValue("RandomizationDate"))
                formApi.setValue('RandomizationDate', Moment(initialValues.RandomizationDate).format(formatString));
            else
                formApi.setValue('RandomizationDate', Moment(new Date()).format(formatString));
        }
    }, [checked, formApi]);

    const validateRandomizedNumber = (subjectNumber, randomizedNumber) => new Promise((resolve) => {
        client.client.getSubjects(undefined, `(site/study/id eq ${studyId}) AND (number ne '${subjectNumber}') AND (randomizationNumber eq '${randomizedNumber}')`).then(subjects => {
            if (subjects.value.length > 0) {
                formApi.setError("RandomizationNumber", "Randomization Id already exist");
            } else
                resolve(null);
        });
    });

    const handleSubmit = async () => {
        formApi.validate();
        let state = formApi.getState();

        let proposedRansomizationNumber: string = state.values.RandomizationNumber;
        
        setSubmitting(true);
        if (state.values.IsRandomized) {
            if (proposedRansomizationNumber === undefined || proposedRansomizationNumber === null) {
                proposedRansomizationNumber = state.values.SubjectNumber;
            }

            await validateRandomizedNumber(state.values.SubjectNumber, proposedRansomizationNumber);
            if(Moment(state.values.RandomizationDate) > Moment(new Date())){
                formApi.setError("RandomizationDate", "Selected Randomization date must less than today's date");
            }
            
            setSubmitting(false);
        }

        
            
        state = formApi.getState();

        if (state.invalid)
            return;

        onSubmit({
            RandomizationNumber: proposedRansomizationNumber,
            RandomizationDate: Moment(state.values.RandomizationDate).format("YYYY-MM-DDTHH:mm:ssZ"),
            IsRandomized: state.values.IsRandomized,
            SubjectNumber: state.values.SubjectNumber,
        });
        setSubmitting(false);
    }

    return (
        <div>
            <ErrorBoundary>
                <div className="modal-body">
                    {children}
                    <div className="form-group">
                        <label htmlFor="subjectNumber">Subject Number</label>
                        <TextBox id="SubjectNumber" field="SubjectNumber" type="text" disabled />
                    </div>

                    <div className="form-group">
                        <Checkbox label="Randomized" id="IsRandomized" field="IsRandomized" onChange={(c) => setChecked(c)} />
                    </div>

                    <ConfigFieldTextBox
                        id="RandomizationNumber"
                        field="RandomizationNumber"
                        className="form-group"
                        hidden={!checked}
                        validated={checked}
                        required={checked}
                        itemId={ConfigurableDataItemIds.RandomizationNumber}
                    />

                    {checked && <ConfigFieldDatePicker
                        id="RandomizationDate"
                        field="RandomizationDate"
                        className="form-group"
                        required={checked}
                        includeTime={true}
                        acceptDateRange={DatePickerAcceptDateRange.PastOrPresent}
                        itemId={ConfigurableDataItemIds.RandomizationDate}
                    />}   
                </div>

                <div className="modal-footer">
                    <AsyncActionButton
                        className="btn btn-primary"
                        type="submit"
                        isSubmitting={submitting}
                        onClick={handleSubmit}
                    >
                        Ok
                        </AsyncActionButton>
                    <button className="btn btn-outline btn-primary" onClick={onClose}>Cancel</button>
                </div>
            </ErrorBoundary>
        </div>
    );
}

export const RandomizationModalBody = (props: React.PropsWithChildren<RandomizationModalBodyProps>) => {
    return (
        <div>
            <Form initialValues={props.initialValues}>
                <RandomizationModalBodyForm  {...props} />
            </Form>
        </div>
    );
}
